import { useCallback, useEffect, useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useToast } from '../components/ui';
import { useTranslation } from '../i18n';
import {
  registerServiceWorker,
  type RegisterServiceWorkerOptions,
  type UpdateServiceWorker,
} from './registerServiceWorker';

/** The prompt stays until the person answers it (24 h is the toast's "forever"). */
export const UPDATE_PROMPT_DURATION_MS = 24 * 60 * 60 * 1000;

export interface PwaUpdaterProps {
  /** Injected in tests; defaults to the real registration. */
  register?: (
    options: RegisterServiceWorkerOptions
  ) => Promise<UpdateServiceWorker | null | undefined> | undefined | null;
}

/** An active game: the update prompt (and any reload) waits until it ends. */
export function isActiveGamePath(pathname: string): boolean {
  return matchPath('/game/:id', pathname) !== null;
}

/**
 * Registers the service worker and, when a new version is waiting, shows an
 * "Update" toast. It never shows or reloads on `/game/:id`: it waits for the
 * route to change. The toast's close button is "Later" (the waiting version
 * is applied the next time the app is closed and reopened).
 */
export function PwaUpdater({
  register = registerServiceWorker,
}: PwaUpdaterProps) {
  const { t } = useTranslation();
  const { showToast, dismissToast } = useToast();
  const { pathname } = useLocation();
  const inGame = isActiveGamePath(pathname);
  const [needRefresh, setNeedRefresh] = useState(false);
  const updateRef = useRef<UpdateServiceWorker | null>(null);
  const toastIdRef = useRef<number | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) {
      return;
    }
    startedRef.current = true;
    try {
      if (typeof register !== 'function') {
        return;
      }
      const pending = register({ onNeedRefresh: () => setNeedRefresh(true) });
      Promise.resolve(pending)
        .then(update => {
          updateRef.current = typeof update === 'function' ? update : null;
        })
        .catch(() => undefined);
    } catch {
      // A broken registration must never take the app down.
    }
  }, [register]);

  const applyUpdate = useCallback(() => {
    toastIdRef.current = null;
    setNeedRefresh(false);
    try {
      const update = updateRef.current;
      if (update) {
        void update(true).catch(() => undefined);
      }
    } catch {
      // Ignore: the new version applies on the next launch anyway.
    }
  }, []);

  useEffect(() => {
    if (!needRefresh) {
      return;
    }
    if (inGame) {
      if (toastIdRef.current !== null) {
        dismissToast(toastIdRef.current);
        toastIdRef.current = null;
      }
      return;
    }
    toastIdRef.current = showToast({
      message: t('pwa.update.available'),
      actionLabel: t('pwa.update.action'),
      onAction: applyUpdate,
      durationMs: UPDATE_PROMPT_DURATION_MS,
    });
  }, [needRefresh, inGame, showToast, dismissToast, t, applyUpdate]);

  return null;
}
